/* eslint-disable eslint-comments/no-restricted-disable */
import type { EntryPoint, EntryPointComponent } from 'react-relay';
import type { EntryPointRouteParams } from '@jira/platform__entry-points-plugin-types/src/types.tsx';
import { JSResourceForUserVisible } from '@atlassian/react-async';
import { createEntryPoint } from '@atlassian/react-entrypoint';
import { expValEquals } from '@atlassian/jira-feature-experiments';
import type {
	JiraJqlScopeInput,
	JiraViewQueryInput,
	JiraBoard_JiraBoardQuery,
} from './__generated__/JiraBoard_JiraBoardQuery.graphql.ts';
import parameters from './__generated__/JiraBoard_JiraBoardQuery$parameters.ts';

const getViewQueryAndScope = (
	cloudId: string,
	projectKey: string,
	boardIdParam: string | null | undefined,
	itemId: string,
): { viewQueryInput: JiraViewQueryInput; filterScope: JiraJqlScopeInput } => {
	const encodedItemId = itemId ? `board/${itemId}` : 'board';

	if (boardIdParam != null) {
		return {
			viewQueryInput: {
				boardIdAndItemQuery: {
					cloudId,
					boardId: Number(boardIdParam),
					itemId: encodedItemId,
				},
			},
			filterScope: {
				board: { boardId: Number(boardIdParam) },
			},
		};
	}

	return {
		viewQueryInput: {
			projectKeyAndItemQuery: {
				cloudId,
				projectKey,
				itemId: encodedItemId,
			},
		},
		filterScope: {
			project: { projectKey },
		},
	};
};

type BoardEntryPointComponent = EntryPointComponent<
	{
		boardQuery: JiraBoard_JiraBoardQuery;
	},
	{},
	{},
	{}
>;

export const boardEntryPoint: EntryPoint<BoardEntryPointComponent, EntryPointRouteParams> =
	createEntryPoint({
		root: JSResourceForUserVisible<BoardEntryPointComponent>(
			/* webpackChunkName: "async-board-entrypoint" */ './JiraBoard.tsx',
			{
				entryFsSsrLiftImportToModule: true,
			},
		),
		getPreloadProps: ({ context, tenantContext: { cloudId } }: EntryPointRouteParams) => {
			const projectKeyParam = context.match.params.projectKey;
			const boardIdParam = context.match.params.boardId;
			const itemId = context.match.params.itemId ?? '';
			const routeName = context.route.name;

			// Either projectKey or boardId must be present
			if (projectKeyParam == null && boardIdParam == null) {
				throw new Error('Project key or board ID is required');
			}
			const withProject = projectKeyParam != null;
			const projectKey = projectKeyParam ?? '';

			let filter: string | undefined;
			let groupBy: string | undefined;

			// if in a client environment, check whether the router url matches the current window pathname
			// if it does, use the filter and groupBy from the current window location instead of the router query
			// this avoids reloading the query without the overrides when a user hovers over prefetching links that don't include them
			const currentLocation = typeof window !== 'undefined' ? window.location : null;

			if (
				currentLocation != null &&
				(context == null || currentLocation.pathname === context?.match.url)
			) {
				const urlSearchParams = new URLSearchParams(currentLocation.search);
				filter = urlSearchParams.get('filter') ?? undefined;
				groupBy = urlSearchParams.get('groupBy') ?? undefined;
			} else if (context) {
				filter = context.query?.filter;
				groupBy = context.query?.groupBy;
			}
			const { viewQueryInput, filterScope } = getViewQueryAndScope(
				cloudId,
				projectKey,
				boardIdParam,
				itemId,
			);

			return {
				queries: {
					boardQuery: {
						options: {
							fetchPolicy: 'store-and-network' as const,
						},
						parameters,
						variables: {
							cloudId,
							projectKey,
							withProject,
							withFilterRefinement:
								routeName === 'classic-business-board' ||
								routeName === 'classic-business-board-embed' ||
								routeName === 'classic-business-board-custom-embed'
									? expValEquals('standalone-filter-refinement-sprt', 'isEnabled', true, false)
									: true,
							viewQueryInput,
							settings: {
								filterJql: filter,
								groupBy,
								swimlanesSupported: expValEquals(
									'board_harmonisation_m3_swimlanes',
									'isEnabled',
									true,
									false,
								),
							},
							filterScope,
						},
					},
				},
			};
		},
	});
